import _ from "lodash";
import { createStripePaymentCheckout } from "../../api";

import {
  gotoExternallyHostedPayment,
  gotoGuestInfo,
  gotoBarclaysPayment,
} from "./";
import { executeMIBook } from "./addons";
import { toast } from "react-toastify";
import { saveBarclaysData } from "../barclays";
import {
  createFellohCheckout,
  createVivaCheckout,
  initBarclaysPay,
} from "../../api/financial";
import { sendPixelEvent } from "../../helpers";

var website_api_key = import.meta.env.VITE_APP_WEBSITE_KEY;

export const paymentLoadingPageDispatcher =
  () => async (dispatch, getState) => {
    const state = getState();
    const payment_supplier = _.get(
      state.WebsiteMetadata,
      "metadata.payment_supplier",
      "STR"
    );

    switch (payment_supplier) {
      case "BCR":
        await dispatch(gotoBarclaysPayment());
        break;
      default:
        await dispatch(gotoExternallyHostedPayment());
        break;
    }
  };

export const onFailedToCreateBookingEntry = ({ history }) => {
  history.push("/");
  toast.error(
    "We are not able to proceed with this booking. We are sorry for the inconvenience.",
    { duration: 5000 }
  );
};

const onPaymentInitFail = () => (dispatch) => {
  toast.error(
    "We couldn't initiate your payment process. We are sorry for the inconvenience.",
    { duration: 5000 }
  );
  dispatch(gotoGuestInfo({ initialize: false }));
};

export const initVivaCheckout =
  ({
    email,
    amount,
    balance_amount,
    balance_due,
    product_name,
    currency,
    description,
    source_entity,
    website_api_key,
    srvType,
    reference,
    first_name,
    last_name,
  }) =>
  async (dispatch) => {
    const payload = {
      amount,
      balance_amount,
      balance_due,
      product_name,
      currency,
      description,
      source_entity,
      website_api_key,
      customer_id: "fil",
      customer_email: email,
      customer_first_name: first_name,
      customer_last_name: last_name,
      service_type: srvType,
      reference,
    };

    const checkoutResult = await createVivaCheckout({ payload });

    if (!checkoutResult) {
      dispatch(onPaymentInitFail());
      return;
    }

    window.location = checkoutResult.url;
  };

export const initStripeHostedPayment =
  ({
    email,
    customer_stripe_id,
    amount,
    balance_amount,
    balance_due_date,
    product_name,
    currency,
    description,
    source_entity,
    website_api_key,
    success_url,
    cancel_url,
    metadata,
  }) =>
  async (dispatch) => {
    const stripe_payload = {
      amount,
      balance_amount,
      balance_due_date,
      product_name,
      currency,
      description,
      source_entity,
      website_api_key,
      success_url,
      cancel_url,
      metadata,
    };

    if (email) {
      stripe_payload["customer_email"] = email;
    }

    if (customer_stripe_id) {
      stripe_payload["customer_stripe_id"] = customer_stripe_id;
    }

    const checkoutResult = await createStripePaymentCheckout(stripe_payload);

    if (!checkoutResult) {
      dispatch(onPaymentInitFail());
      return;
    }

    window.location = checkoutResult.redirect_url;
  };

export const initFellohPayment =
  ({
    first_name,
    last_name,
    booking_reference,
    email,
    amount,
    balance_amount,
    balance_due_date,
    product_name,
    currency,
    description,
    source_entity,
    success_url,
    cancel_url,
    srv_type,
  }) =>
  async (dispatch) => {
    const customer_name = `${first_name} ${last_name}`;

    const payload = {
      amount,
      balance_amount,
      balance_due_date,
      product_name,
      currency,
      description,
      source_entity,
      customer_name,
      email,
      booking_id: null,
      open_banking_enabled: true,
      card_enabled: true,
      expires_at: null,
      authorisation_only: false,
      booking_data: {
        customer_name,
        email,
        gross_amount: amount,
        start_date: null,
        end_date: null,
        booking_reference,
        srv_type,
      },
    };

    const checkoutResult = await createFellohCheckout({ payload });

    if (!checkoutResult) {
      dispatch(onPaymentInitFail());
      return;
    }

    window.location = checkoutResult.payment_link;
  };

export const initBarclaysPayment =
  ({
    page_session_id,
    source_entity,
    website_api_key,
    email,
    amount,
    currency,
    booking_reference,
    first_name,
    last_name,
    order_id,
    success_url,
    cancel_url,
    fail_url,
  }) =>
  async (dispatch) => {
    const payload = {
      source_entity,
      website_api_key,
      EMAIL: email,
      AMOUNT: amount * 100,
      CURRENCY: currency,
      ORDERID: order_id,
      LANGUAGE: "en",
      CN: `${first_name} ${last_name}`,
      ACCEPTURL: success_url,
      DECLINEURL: fail_url,
      EXCEPTIONURL: fail_url,
      CANCELURL: cancel_url,
    };
    const barclaysResult = await initBarclaysPay({ payload });

    if (!barclaysResult) {
      dispatch(onPaymentInitFail());
      return;
    }

    dispatch(
      saveBarclaysData({
        data: {
          ...payload,
          PSPID: barclaysResult.PSPID,
          SHASIGN: barclaysResult.request_data,
          redirect_url: barclaysResult.redirect_url,
        },
      })
    );
  };

export const paymentDispatcher =
  ({
    booking_reference,
    page_session_id,
    amount,
    deposit_amount,
    balance_amount,
    balance_due_date,
    product_name,
    currency,
    first_name,
    last_name,
    email,
    customer_uid,
    nationality,
    customer_stripe_id,
    order_id,
    success_url,
    cancel_url,
    fail_url,
    srv_type,
    source_entity,
  }) =>
  async (dispatch, getState) => {
    const state = getState();

    const payment_supplier = _.get(
      state.WebsiteMetadata,
      "metadata.payment_supplier",
      "STR"
    );
    const websiteName = state?.WebsiteMetadata?.metadata?.name ?? "";

    const srvLabelMapping = {
      MI: "Tour",
      ACT: "Activity",
      ACC: "Hotel",
      FL: "Flight",
      TRF: "Transfer",
      FER: "Ferry",
      TRA: "Train",
      CAR: "Car Rental",
    };
    const description = `${websiteName} - ${_.get(
      srvLabelMapping,
      srv_type
    )} Reservation with reference: ${booking_reference}`;

    const common_payment_payload = {
      page_session_id,
      email,
      amount: deposit_amount ? deposit_amount : amount,
      balance_amount: balance_amount || 0,
      balance_due_date: balance_due_date || null,
      product_name: product_name || description,
      currency,
      srv_type,
      reference: booking_reference,
      source_entity,
      website_api_key,
      success_url,
      cancel_url,
    };

    switch (payment_supplier) {
      case "BCR": {
        await dispatch(
          initBarclaysPayment({
            ...common_payment_payload,
            booking_reference,
            first_name,
            last_name,
            customer_uid,
            nationality,
            order_id,
            fail_url,
          })
        );
        break;
      }
      case "STR": {
        await dispatch(
          initStripeHostedPayment({
            ...common_payment_payload,
            customer_stripe_id,
            description,
            metadata: {
              srv_type,
              reference: booking_reference,
            },
          })
        );
        break;
      }
      case "FEL": {
        await dispatch(
          initFellohPayment({
            ...common_payment_payload,
            first_name,
            last_name,
            booking_reference,
            description,
            srv_type,
          })
        );
        break;
      }
      case "VIV": {
        dispatch(
          initVivaCheckout({
            ...common_payment_payload,
            description,
            first_name,
            last_name,
            srvType: srv_type,
            reference: booking_reference,
          })
        );
        break;
      }
      default:
        break;
    }
  };

export const initMIPaymentProcess =
  ({
    history,
    page_session_id,
    first_name,
    last_name,
    email,
    customer_uid,
    nationality,
    customer_stripe_id,
    guestsList,
    booked_rooms,
    source_entity,
    website_api_key,
    remarks,
  }) =>
  async (dispatch, getState) => {
    const state = getState();

    const { price_info } = state.TourPriceInfo;

    const amount = price_info.price[0].value;

    const deposit_amount = _.get(price_info, "price.0.deposit_amount", 0);
    const balance_amount = _.get(price_info, "price.0.balance_amount", 0);
    const balance_due_date = _.get(price_info, "price.0.balance_due", null);
    const product_name = _.get(price_info, "price.0.title", "");

    const currency = price_info.price[0].currency;

    sendPixelEvent("InitiateCheckout", {
      data: { product_type: "addon" },
      withPageView: false,
    });

    //Goto relevant payment loading page.
    dispatch(paymentLoadingPageDispatcher());

    if (booked_rooms.length) {
      guestsList.forEach((g) => {
        const [room_uid, ___] = booked_rooms
          .find((r) => g.room_idx_uid === r.room_idx_uid)
          .room_idx_uid.split("---");
        g["room_uid"] = room_uid;
      });
    }

    const booking_result = await dispatch(
      executeMIBook({
        customer_uid,
        email,
        first_name,
        last_name,
        nationality,
        guestsList,
        booked_rooms,
        source_entity,
        website_api_key,
        remarks,
      })
    );

    if (!booking_result) {
      onFailedToCreateBookingEntry({ history });
      return;
    }

    const booking_reference = _.get(booking_result, "booking_result.reference");

    const srv_type = "MI";
    const order_id = `${srv_type}___${booking_reference}`;

    const base_url = window.location.origin;
    const success_url = `${base_url}/tour/successful-booking/${page_session_id}`;
    const cancel_url = `${base_url}/tour/booking_process/${page_session_id}`;
    const fail_url = `${base_url}/failed-booking/${page_session_id}`;

    dispatch(
      paymentDispatcher({
        booking_reference,
        page_session_id,
        amount,
        deposit_amount,
        balance_amount,
        balance_due_date,
        product_name,
        currency,
        first_name,
        last_name,
        email,
        customer_uid,
        nationality,
        customer_stripe_id,
        order_id,
        success_url,
        cancel_url,
        fail_url,
        srv_type,
        source_entity,
      })
    );
  };
